import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { SellerOrderDetail, SellerOrderListItem } from "@/lib/api/types";
import { queryKeys } from "./query-keys";

type InvalidationTarget = {
  queryKey: QueryKey;
  exact?: boolean;
};

async function invalidateTargets(
  queryClient: QueryClient,
  targets: InvalidationTarget[],
) {
  await Promise.all(
    targets.map((target) =>
      queryClient.invalidateQueries({
        queryKey: target.queryKey,
        exact: target.exact,
      }),
    ),
  );
}

export async function invalidateSellerSession(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.session(storeSlug), exact: true },
    { queryKey: queryKeys.seller.profile(storeSlug), exact: true },
  ]);
}

export async function invalidateSellerCatalog(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.categoriesPrefix(storeSlug) },
    { queryKey: queryKeys.seller.productsPrefix(storeSlug) },
    // Refresh the onboarding overview (hasCategories / hasProducts) so the
    // setup checklist drops a step the moment it's completed.
    { queryKey: queryKeys.seller.session(storeSlug), exact: true },
  ]);
}

export async function invalidateSellerStore(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.store(storeSlug), exact: true },
    { queryKey: queryKeys.seller.settings(storeSlug), exact: true },
    { queryKey: queryKeys.seller.storeSettings(storeSlug), exact: true },
    { queryKey: queryKeys.seller.storeSubscription(storeSlug), exact: true },
    { queryKey: queryKeys.seller.paymentOptionsPrefix(storeSlug) },
  ]);
}

export async function invalidateSellerShippingRules(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.shippingRulesPrefix(storeSlug) },
  ]);
}

export async function invalidateSellerFreeShippingRules(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.freeShippingRulesPrefix(storeSlug) },
  ]);
}

export async function invalidateSellerCustomDomain(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.customDomain(storeSlug) },
  ]);
}

export async function invalidateSellerOrders(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.ordersPrefix(storeSlug) },
    { queryKey: queryKeys.seller.orderStatusCounts(storeSlug), exact: true },
  ]);
}

/**
 * M1 — surgical single-order cache patch.
 *
 * Applies `patch` in-place to every cached view that might contain the given
 * order, without triggering a refetch.  Handles three shapes:
 *
 *  1. orderById detail entry  → merge patch directly onto the cached object.
 *  2. Plain-list caches       → data has `.items: SellerOrderListItem[]`
 *     (useOrdersQuery / useQuery).  Map items and replace the matching id.
 *  3. Infinite-query caches   → data has `.pages[].items`
 *     (useOrdersInfiniteQuery).  Map each page's items array.
 *
 * Use this for single-order field updates (status, eta).  Use
 * `invalidateSellerOrders` for create / delete operations where row-count
 * membership or totals must be recomputed server-side.
 */
export function patchSellerOrderInCache(
  queryClient: QueryClient,
  storeSlug: string,
  orderId: string,
  patch: Partial<SellerOrderListItem>,
) {
  // 1. orderById detail — merge patch onto the full SellerOrderDetail.
  queryClient.setQueryData<SellerOrderDetail>(
    queryKeys.seller.orderById(storeSlug, orderId),
    (old) => (old ? { ...old, ...patch } : old),
  );

  // 2 + 3. All list/infinite caches under the orders namespace.
  //
  // setQueriesData matches by key prefix so it covers both useQuery list keys
  // (["seller", slug, "orders", "list", filters]) and the infinite key
  // (["seller", slug, "orders", "smart"]) as well as any future shapes.
  queryClient.setQueriesData<
    // Plain list shape
    | { items: SellerOrderListItem[]; total: number; limit: number; offset: number; nextCursor?: string }
    // Infinite shape
    | { pages: { items: SellerOrderListItem[]; nextCursor?: string | null }[]; pageParams: unknown[] }
  >(
    { queryKey: queryKeys.seller.ordersPrefix(storeSlug) },
    (old) => {
      if (!old) return old;

      // Plain-list shape: top-level `.items` array.
      if ("items" in old) {
        const next = old.items.map((item) =>
          item.id === orderId ? { ...item, ...patch } : item,
        );
        // Avoid a new object reference when nothing changed.
        return next === old.items ? old : { ...old, items: next };
      }

      // Infinite shape: `.pages[].items`.
      if ("pages" in old) {
        let changed = false;
        const pages = old.pages.map((page) => {
          const nextItems = page.items.map((item) => {
            if (item.id !== orderId) return item;
            changed = true;
            return { ...item, ...patch };
          });
          return nextItems === page.items ? page : { ...page, items: nextItems };
        });
        return changed ? { ...old, pages } : old;
      }

      return old;
    },
  );
}

export async function invalidateSellerPaymentOptions(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.paymentOptionsPrefix(storeSlug) },
    { queryKey: queryKeys.seller.store(storeSlug), exact: true },
    // Refresh the onboarding overview (hasActivePaymentOptions) so the setup
    // checklist drops the payment step the moment an active option exists.
    { queryKey: queryKeys.seller.session(storeSlug), exact: true },
  ]);
}

// Shipment mutations also refresh the order (status may have moved) and the
// orders list (status chips).
export async function invalidateOrderShipment(
  queryClient: QueryClient,
  storeSlug: string,
  orderId: string,
) {
  await invalidateTargets(queryClient, [
    {
      queryKey: queryKeys.seller.orderShipment(storeSlug, orderId),
      exact: true,
    },
    { queryKey: queryKeys.seller.orderById(storeSlug, orderId), exact: true },
    { queryKey: queryKeys.seller.ordersPrefix(storeSlug) },
  ]);
}

// A refund moves the order money-state and the payment transaction; refresh
// the transaction, the order, and the orders list (chips).
export async function invalidateOrderPayment(
  queryClient: QueryClient,
  storeSlug: string,
  orderId: string,
) {
  await invalidateTargets(queryClient, [
    {
      queryKey: queryKeys.seller.orderPaymentTransaction(storeSlug, orderId),
      exact: true,
    },
    { queryKey: queryKeys.seller.orderById(storeSlug, orderId), exact: true },
    { queryKey: queryKeys.seller.ordersPrefix(storeSlug) },
  ]);
}

export async function invalidateSellerJntConnection(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.jntConnection(storeSlug), exact: true },
  ]);
}

export async function invalidateSellerBostaConnection(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.bostaConnection(storeSlug), exact: true },
  ]);
}

export async function invalidateSellerPaymentGatewayConnection(
  queryClient: QueryClient,
  storeSlug: string,
  provider: string,
) {
  await invalidateTargets(queryClient, [
    {
      queryKey: queryKeys.seller.paymentGatewayConnection(storeSlug, provider),
      exact: true,
    },
  ]);
}

export async function invalidateSellerCoupons(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.couponsPrefix(storeSlug) },
  ]);
}

export async function invalidateSellerRoles(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.rolesPrefix(storeSlug) },
  ]);
}

export async function invalidateSellerMembers(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.membersPrefix(storeSlug) },
  ]);
}

export async function invalidateSellerInvites(
  queryClient: QueryClient,
  storeSlug: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.seller.invitesPrefix(storeSlug) },
  ]);
}
